/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { center as turf_center } from '@turf/turf';
import type { Feature, Position } from 'geojson';
import { debounce } from 'lodash';
import { View } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { unByKey } from 'ol/Observable';
import { transform, transformExtent } from 'ol/proj';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { LocationSearchTypes } from '@opengeoweb/core';
import { AreaFilter } from './AreaFilter';
import { AreaFilterLocationSearch } from './AreaFilterLocationSearch';
import { dataExplorerActions, dataExplorerSelectors } from '../../store';
import type { DataExplorerModuleState } from '../../store/types';

const initialView = {
  projection: 'EPSG:3857',
  zoom: 0,
  center: [0, 0],
};

// Normalize longitude values so that they are between -180 and 180 degrees
const normalizeLongitude = (lon: number): number => {
  let normalized = lon;

  while (normalized < -180) {
    normalized += 360;
  }
  while (normalized > 180) {
    normalized -= 360;
  }

  return normalized;
};

export const AreaFilterConnect: React.FC = () => {
  const dispatch = useDispatch();

  const tileURL = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getAreaFilterTileURL(store),
  );

  const tileLicense = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getAreaFilterTileLicense(store),
  );

  const view = useRef<View>(new View(initialView));

  const [feature, setFeature] = useState<Feature>();

  const selectedLocation = React.useRef<LocationSearchTypes.LocationDetail>();

  const debouncedSetBbox = useMemo(
    () =>
      debounce(() => {
        const zoom = view.current.getZoom();
        // Some areas are cut out around the edges of the map even when zoomed out
        // So at low zoom levels, set the bbox to the whole world
        if (zoom !== undefined && zoom < 0.3) {
          dispatch(dataExplorerActions.setBbox({ bbox: [-180, -90, 180, 90] }));
        } else {
          const extent = view.current.calculateExtent();
          const transformedExtent = transformExtent(
            extent,
            view.current.getProjection(),
            'EPSG:4326',
          );

          // The map can be panned left or right beyond the -180/180 longitude range, so values need to be normalized
          transformedExtent[0] = normalizeLongitude(transformedExtent[0]);
          transformedExtent[2] = normalizeLongitude(transformedExtent[2]);

          dispatch(dataExplorerActions.setBbox({ bbox: transformedExtent }));
        }
      }, 1000),
    [dispatch, view],
  );

  const setBbox = useCallback((): void => {
    debouncedSetBbox();
  }, [debouncedSetBbox]);

  React.useEffect(() => {
    if (!view) {
      return (): void => {};
    }

    const eventKey = view.current.on('change', () => {
      setBbox();
    });

    return (): void => {
      unByKey(eventKey);
    };
  }, [setBbox, view]);

  const selectLocation = useCallback(
    (location?: LocationSearchTypes.LocationDetail): void => {
      if (location && location.id === selectedLocation.current?.id) {
        return;
      }
      if (location) {
        // We need to get three properties from the location:
        // Center coordinate for the map view
        // Resolution for the zoom
        // Feature with a geometry to display on the map
        const { center, resolution, locationFeature } = getLocationProperties(
          location,
          view.current,
        );

        setFeature(locationFeature);

        selectedLocation.current = location;
        const transformedCenter = transform(
          center,
          'EPSG:4326',
          view.current.getProjection(),
        );
        view.current.animate({
          center: transformedCenter,
          resolution,
          duration: 200,
        });
      } else {
        setFeature(undefined);
      }
    },
    [selectedLocation, view],
  );

  return (
    <>
      <AreaFilterLocationSearch onSelectLocation={selectLocation} />
      <AreaFilter
        feature={feature}
        tileLicense={tileLicense}
        tileURL={tileURL}
        view={view.current}
      />
    </>
  );
};

const getLocationProperties = (
  location: LocationSearchTypes.LocationDetail,
  view: View,
): {
  resolution: number | undefined;
  center: Position;
  locationFeature: Feature | undefined;
} => {
  // If the location has a bounding box, fit the view to it
  if (location.bbox) {
    return getBboxLocationProperties(location, view);
  }
  // If the location has a geometry, calculate bounding box for it and fit the view
  if (location.geometry) {
    const props = getGeometryLocationProperties(location, view);

    if (props) {
      return props;
    }
  }

  // Fall back to only the coordinates and the current resolution if there is no bbox or geometry
  const coords = [location.lon, location.lat];
  const center = coords;
  const resolution = view.getResolution();

  return { center, resolution, locationFeature: undefined };
};

const getBboxLocationProperties = (
  location: LocationSearchTypes.LocationDetail,
  view: View,
): {
  resolution: number;
  center: Position;
  locationFeature: Feature;
} => {
  const transformedBbox = transformExtent(
    location.bbox!,
    'EPSG:4326',
    view.getProjection(),
  );
  const resolution = view.getResolutionForExtent(transformedBbox);
  const [west, south, east, north] = location.bbox!;
  const bboxFeature = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [west, south],
          [west, north],
          [east, north],
          [east, south],
          [west, south],
        ],
      ],
    },
  } as Feature;

  const center = turf_center(bboxFeature).geometry.coordinates;

  return { resolution, center, locationFeature: bboxFeature };
};

const getGeometryLocationProperties = (
  location: LocationSearchTypes.LocationDetail,
  view: View,
): {
  resolution: number;
  center: Position;
  locationFeature: Feature;
} | null => {
  const geojsonFeature = new GeoJSON().readFeature(location.geometry);

  if (geojsonFeature) {
    const geometry = Array.isArray(geojsonFeature)
      ? geojsonFeature[0]?.getGeometry()
      : geojsonFeature.getGeometry();

    if (geometry) {
      const extent = geometry.getExtent();

      const transformedBbox = transformExtent(
        extent,
        'EPSG:4326',
        view.getProjection(),
      );
      const resolution = view.getResolutionForExtent(transformedBbox);

      const center = turf_center(location.geometry!).geometry.coordinates;

      return { resolution, center, locationFeature: location.geometry! };
    }
  }

  return null;
};
